/**
 * Width plumbing shared by EmbedHost (provider) and every component
 * whose layout depends on how much room it actually has.
 *
 * The embed is dropped into customer pages and dashboard cards of any
 * width, so the *viewport* says nothing about the space an insight
 * renders into — a 360px card on a 1440px desktop is a phone layout.
 * Everything here measures elements, never the window.
 *
 * Two layers:
 *
 *   1. `provideEmbedWidth` / `useEmbedWidth` — one ResizeObserver on
 *      EmbedHost's root, exposed as a reactive width plus a coarse
 *      bucket. Components read `bucket` when they only care which side
 *      of a breakpoint the whole embed sits on.
 *   2. `useElementWidth` — a component measuring its OWN element. A
 *      chart in a 4-up column cell is far narrower than the embed.
 *
 * Print: the PDF pins the embed to 180mm ≈ 680px
 * (`pdf/print-template.ts`), so both breakpoints sit below it and
 * `bucket` is forced to "wide" in print. Element widths are NOT
 * forced — a print column cell really is narrow, and pure layout
 * (label widths, wrapping) should adapt to it. Mobile *behaviour*
 * (sheets, collapsed columns, zoom sliders) must still check
 * `usePrintMode()`.
 *
 * A width of 0 means "not measured yet" (happy-dom, first frame).
 * Callers treat 0 as wide so unmeasured renders match the desktop
 * output.
 *
 * Like `print-mode.ts`, keep this file dependency-free beyond Vue —
 * it is imported by nearly every component.
 */

import {
  computed,
  inject,
  onBeforeUnmount,
  onMounted,
  provide,
  shallowRef,
  watch,
  type InjectionKey,
  type Ref,
  type WatchStopHandle,
} from "vue";

export type EmbedWidthBucket = "narrow" | "compact" | "wide";

/** Breakpoints, px. Both must stay below the ~680px PDF width. */
export const EMBED_BP = { narrow: 480, compact: 640 } as const;

export const bucketFor = (width: number): EmbedWidthBucket =>
  width <= 0
    ? "wide"
    : width < EMBED_BP.narrow
      ? "narrow"
      : width < EMBED_BP.compact
        ? "compact"
        : "wide";

export interface EmbedWidth {
  /** Measured inline size of the embed root, px. 0 = unmeasured. */
  width: Readonly<Ref<number>>;
  /** Bucket for `width`; always "wide" in print. */
  bucket: Readonly<Ref<EmbedWidthBucket>>;
}

export const EMBED_WIDTH_KEY: InjectionKey<EmbedWidth> = Symbol(
  "lucent:embed-width",
);

const FALLBACK_WIDE: EmbedWidth = {
  width: shallowRef(0),
  bucket: computed(() => "wide" as const),
};

type MeasureTarget = Element | { $el?: unknown } | null | undefined;

const resolveElement = (target: MeasureTarget): Element | null => {
  if (!target) return null;
  if (typeof Element !== "undefined" && target instanceof Element) {
    return target;
  }
  const el = (target as { $el?: unknown }).$el;
  return typeof Element !== "undefined" && el instanceof Element ? el : null;
};

export interface ElementWidthOptions {
  /**
   * Quantisation step, px. The ref only changes when the floored
   * width crosses a step boundary. Charts use 16: vue-echarts
   * replaces the whole option on every rebuild, which resets legend
   * toggles and zoom, so rebuilding per pixel of resize is wrong.
   */
  step?: number;
  /** Width to report before the element mounts. Defaults to 0. */
  initial?: () => number;
}

/**
 * Reactive inline size of `target` (an element or a component
 * instance — `$el` is unwrapped). Read synchronously on mount so the
 * first paint already has a width, then kept current by a
 * ResizeObserver coalesced to one update per animation frame.
 * Height-only changes never update the ref. If the target element is
 * swapped or appears later (a `v-if` branch), the observer follows it.
 */
/**
 * Content-box inline size, the same box the ResizeObserver reports.
 * `clientWidth` includes padding, so reading it raw on mount gave a padded
 * target (a KPI tile) two different widths in its first two frames.
 */
const contentWidth = (el: Element): number => {
  const w = el.clientWidth;
  if (typeof getComputedStyle !== "function") return w;
  const cs = getComputedStyle(el);
  const pad = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
  return Math.max(0, w - pad);
};

export function useElementWidth(
  target: Ref<MeasureTarget>,
  opts: ElementWidthOptions = {},
): Readonly<Ref<number>> {
  const step = Math.max(1, opts.step ?? 1);
  const quantise = (w: number): number => Math.floor(Math.round(w) / step) * step;
  const width = shallowRef(quantise(opts.initial?.() ?? 0));

  let observer: ResizeObserver | null = null;
  let frame: number | null = null;
  let latest = 0;

  const apply = (): void => {
    frame = null;
    const next = quantise(latest);
    if (next !== width.value) width.value = next;
  };

  let stopWatch: WatchStopHandle | null = null;

  onMounted(() => {
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver((entries) => {
        const entry = entries[entries.length - 1];
        if (!entry) return;
        const box = entry.contentBoxSize?.[0];
        latest = box ? box.inlineSize : entry.contentRect.width;
        if (frame !== null) return;
        frame =
          typeof requestAnimationFrame === "function"
            ? requestAnimationFrame(apply)
            : (apply(), null);
      });
    }
    stopWatch = watch(
      () => resolveElement(target.value),
      (el, prev) => {
        if (prev) observer?.unobserve(prev);
        if (!el) return;
        latest = contentWidth(el);
        apply();
        observer?.observe(el);
      },
      { immediate: true, flush: "post" },
    );
  });

  onBeforeUnmount(() => {
    stopWatch?.();
    stopWatch = null;
    observer?.disconnect();
    observer = null;
    if (frame !== null && typeof cancelAnimationFrame === "function") {
      cancelAnimationFrame(frame);
    }
    frame = null;
  });

  return width;
}

/**
 * Called once in EmbedHost's setup. `el` is the root wrapper; the
 * observer attaches on mount.
 */
export function provideEmbedWidth(
  el: Ref<MeasureTarget>,
  isPrint: () => boolean,
): EmbedWidth {
  const width = useElementWidth(el);
  const bucket = computed<EmbedWidthBucket>(() =>
    isPrint() ? "wide" : bucketFor(width.value),
  );
  const value: EmbedWidth = { width, bucket };
  provide(EMBED_WIDTH_KEY, value);
  return value;
}

/**
 * Read the embed width from the nearest EmbedHost. Falls back to an
 * unmeasured, always-wide value so components mounted in isolation
 * (unit tests) render their desktop layout.
 */
export const useEmbedWidth = (): EmbedWidth =>
  inject(EMBED_WIDTH_KEY, FALLBACK_WIDE);

/**
 * Reactive `matchMedia`. Reserved for genuinely viewport-level
 * decisions — UI sized against the screen (modal sheets) and device
 * capabilities (`(pointer: coarse)`). Layout must use element width.
 */
export function useMediaQuery(query: string): Readonly<Ref<boolean>> {
  const matches = shallowRef(false);
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return matches;
  }
  const mql = window.matchMedia(query);
  matches.value = mql.matches;
  const sync = (e: MediaQueryListEvent | MediaQueryList): void => {
    matches.value = e.matches;
  };
  // Older Safari only has `addListener`; modern browsers expose
  // `addEventListener("change", …)`.
  if (typeof mql.addEventListener === "function") {
    mql.addEventListener("change", sync);
  } else {
    (mql as { addListener?: (cb: (e: MediaQueryListEvent) => void) => void })
      .addListener?.(sync);
  }
  onBeforeUnmount(() => {
    if (typeof mql.removeEventListener === "function") {
      mql.removeEventListener("change", sync);
    } else {
      (mql as { removeListener?: (cb: (e: MediaQueryListEvent) => void) => void })
        .removeListener?.(sync);
    }
  });
  return matches;
}
